<script setup lang="ts">
import { computed } from 'vue'

import { safeAreaTopOffset } from '@/services/safeAreaService'
import { useNavigationStore } from '@/stores/navigationStore'
import { useUiStore } from '@/stores/uiStore'
import { Z_INDEX } from '@/utils/zIndex'

defineOptions({ name: 'MapBrandWordmark' })

// Decorative, ambient brand wordmark "routecamp" debossed into the map surface at top-center —
// quiet brand presence without a banner. Purely presentational and non-interactive (map gestures
// pass straight through). Shown during normal map browsing AND in immersive/full-map mode (the
// quiet brand presence belongs in the immersive view too); hidden whenever the top is genuinely
// occupied or the chrome is suppressed (search, track draw/edit, navigation, landing).
const uiStore = useUiStore()
const navigationStore = useNavigationStore()

const isVisible = computed(
  () =>
    !uiStore.isLandingPageVisible &&
    !uiStore.isSmartSearchOpen &&
    !uiStore.isTrackDrawingActive &&
    !uiStore.isTrackEditMode &&
    !navigationStore.isActive,
)

// Vertically centre the wordmark on the sync button's row: the button is 44px tall at
// safe-area-top + 12px, so its centre is at safe-area-top + 34px. The wrapper is centred on this
// point via translate(-50%, -50%), so the two read as aligned on one line.
const topOffset = safeAreaTopOffset(34)
</script>

<template>
  <div
    v-show="isVisible"
    class="brand-wordmark"
    :style="{ top: topOffset, zIndex: Z_INDEX.MAP_WORDMARK }"
    aria-hidden="true"
  >
    <span class="brand-wordmark__text">routecamp</span>
  </div>
</template>

<style scoped>
.brand-wordmark {
  position: fixed;
  left: 50%;
  /* Centre the wordmark's own centre on `top` (the sync button's vertical centre). */
  transform: translate(-50%, -50%);
  pointer-events: none;
  user-select: none;
}

.brand-wordmark__text {
  /* Inherits IA Writer Quattro from the global font-sans default. */
  font-weight: 700;
  font-size: 15px;
  line-height: 1;
  /* Airy tracking reads as a wordmark; text-indent re-centres the glyphs (trailing letter-spacing
     otherwise pads the right edge and shifts the visible text left of centre). */
  letter-spacing: 0.22em;
  text-indent: 0.22em;
  /* Dark slate fill (~80% black) — a confident, readable wordmark that still lets a hint of the map
     texture through the glyphs. */
  color: rgba(15, 23, 42, 0.82);
  /* Crisp letterpress deboss for the light basemap: a tight legibility halo, a sharp 1px white
     highlight below, and a 1px dark recess above — etched in rather than glowing on top. */
  text-shadow:
    0 0 7px rgba(255, 255, 255, 0.6),
    0 1px 0 rgba(255, 255, 255, 0.9),
    0 -1px 0 rgba(15, 23, 42, 0.26);
}

@media (min-width: 768px) {
  .brand-wordmark__text {
    font-size: 17px;
  }
}
</style>
